import { useDraggable } from "@dnd-kit/core";
import { Building2, CircleX, Flag, RotateCcw, User as UserIcon } from "lucide-react";
import * as React from "react";

import { Svg } from "@/assets";
import { Badge, Card } from "@/components/ui";
import { User, VideoProject } from "@/services/bedrock";
import { cn } from "@/lib/utils";
import { extractInitials } from "@/utils";

import { resolveStaffUser, staffDisplayName } from "@/components/VideoProjectStaffPicker";
import { VideoProjectStatusIcon } from "@/components/VideoProjectStatusPicker";
import { VideoProjectKanbanCard } from "./VideoProjectKanbanCard";
import { getKanbanColumnId, KanbanColumnId } from "./kanbanColumns";

interface KanbanCardProps {
  videoProject: VideoProject;
  staffUsers: User[];
  currentUserId?: number;
  disabled?: boolean;
  draggable?: boolean;
  collapseInPlace?: boolean;
  isDragSource?: boolean;
  selected?: boolean;
  onAssigneeChange?: (project: VideoProject, userId: number | null) => void;
  onQaChange?: (project: VideoProject, userId: number | null) => void;
  onStatusChange?: (project: VideoProject, columnId: KanbanColumnId) => void;
}

export const KanbanCard = React.memo(function KanbanCard({
  videoProject,
  staffUsers,
  currentUserId,
  disabled = false,
  draggable = true,
  collapseInPlace = false,
  isDragSource = false,
  selected = false,
  onAssigneeChange,
  onQaChange,
  onStatusChange,
}: KanbanCardProps) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: String(videoProject.id),
    disabled: disabled || !draggable,
  });

  return (
    <div
      ref={setNodeRef}
      data-kanban-card=""
      data-kanban-project-id={videoProject.id}
      className={cn(
        "touch-none outline-none",
        (isDragging || isDragSource) && "opacity-0",
        collapseInPlace && "hidden"
      )}
      {...attributes}
      {...listeners}
      tabIndex={-1}
    >
      <VideoProjectKanbanCard
        videoProject={videoProject}
        staffUsers={staffUsers}
        currentUserId={currentUserId}
        disabled={disabled}
        selected={selected}
        onAssigneeChange={onAssigneeChange}
        onQaChange={onQaChange}
        onStatusChange={onStatusChange}
      />
    </div>
  );
});

function overlayStaffDot(user: User | undefined) {
  const initials = user ? extractInitials(staffDisplayName(user)) : "";
  const photoUrl = user?.photoUrl;
  return (
    <span
      className={cn(
        "relative flex size-6 shrink-0 items-center justify-center overflow-hidden rounded-full text-[9px] font-medium shadow-sm",
        photoUrl ? "bg-muted" : user ? "bg-sky-600 text-white" : "bg-muted text-muted-foreground"
      )}
    >
      {photoUrl ? (
        <img src={photoUrl} alt="" className="size-full object-cover" />
      ) : user ? (
        initials
      ) : (
        <UserIcon className="size-4" />
      )}
    </span>
  );
}

export const KanbanCardOverlay = React.memo(function KanbanCardOverlay({
  videoProject,
  staffUsers,
}: {
  videoProject: VideoProject;
  staffUsers: User[];
}) {
  const assignments = videoProject.queueAssignments ?? [];
  const assignee = resolveStaffUser(videoProject.editorUserId, staffUsers, assignments);
  const qaUser = resolveStaffUser(videoProject.qaUserId, staffUsers, assignments);
  const isPriority = Boolean(videoProject.isPriority);
  const showPriorityAccent = isPriority && getKanbanColumnId(videoProject.status) !== "completed";

  return (
    <div className="pointer-events-none cursor-grabbing origin-center rotate-3 scale-105 opacity-80 shadow-xl">
      <Card
        size="sm"
        className={cn(
          "relative gap-0 overflow-hidden data-[size=sm]:py-0 ring-foreground/10",
          showPriorityAccent &&
            "before:absolute before:inset-y-0 before:left-0 before:w-1 before:rounded-l-[inherit] before:bg-sky-500",
          videoProject.needsAck && "ring-amber-500"
        )}
      >
        <div className="relative z-10 flex flex-col gap-2 p-2.5">
          <div className="flex items-center justify-between gap-2">
            <span className="text-muted-foreground flex min-w-0 items-center gap-1 font-mono text-[11px]">
              {isPriority ? <Flag className="size-3 shrink-0 fill-sky-500 text-sky-500" aria-hidden="true" /> : null}#
              {videoProject.id}
            </span>
            <div className="flex shrink-0 items-center gap-1.5">
              {overlayStaffDot(assignee)}
              {overlayStaffDot(qaUser)}
            </div>
          </div>
          <div className="flex items-start gap-2.5">
            <div className="bg-muted size-16 shrink-0 overflow-hidden rounded-md">
              <img
                src={videoProject.thumbnailUrl || Svg.PromptPlaceholder}
                alt=""
                className={cn(
                  "size-full",
                  videoProject.thumbnailUrl ? "object-cover object-center" : "object-none object-center"
                )}
              />
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
              <div className="flex items-start gap-1">
                <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center">
                  <VideoProjectStatusIcon status={videoProject.status} />
                </span>
                <div className="text-foreground line-clamp-2 min-w-0 pt-0.5 text-left text-sm leading-snug font-medium tracking-tight">
                  {videoProject.playerName || "Untitled Player"}
                </div>
              </div>
              {videoProject.companyName || videoProject.isTestProject ? (
                <div className="flex min-w-0 flex-wrap items-center gap-1">
                  {videoProject.isTestProject ? (
                    <Badge
                      variant="outline"
                      className="h-5 gap-1 border-amber-200 bg-amber-50 px-1.5 py-0 text-[10px] font-medium text-amber-800 shadow-none"
                    >
                      Demo
                    </Badge>
                  ) : null}
                  {videoProject.companyName ? (
                    <Badge
                      variant="outline"
                      className="text-muted-foreground h-5 max-w-full gap-1 px-1.5 py-0 text-[10px] font-medium shadow-none"
                    >
                      <Building2 className="size-2.5 shrink-0 text-orange-500" />
                      <span className="truncate">{videoProject.companyName}</span>
                    </Badge>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
          {videoProject.qaStatus === "REJECTED" || (videoProject.revisionCount && videoProject.revisionCount > 0) ? (
            <div className="flex items-center justify-end gap-1">
              {videoProject.qaStatus === "REJECTED" ? (
                <Badge
                  variant="outline"
                  className="h-5 shrink-0 gap-1 border-red-200 bg-red-50 px-1.5 py-0 text-[10px] font-medium text-red-800 shadow-none"
                  aria-label="QA failed"
                >
                  <CircleX className="size-2.5" />
                  QA
                </Badge>
              ) : null}
              {videoProject.revisionCount && videoProject.revisionCount > 0 ? (
                <Badge variant="outline" className="h-5 shrink-0 gap-1 px-1.5 py-0 text-[10px] font-medium shadow-none">
                  <RotateCcw className="size-2.5" />
                  {videoProject.revisionCount}
                </Badge>
              ) : null}
            </div>
          ) : null}
        </div>
      </Card>
    </div>
  );
});
